import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Lesson, Unit } from '@/types'
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  BookOpen,
  FileText,
  AlertCircle,
  Filter
} from 'lucide-react'

export default function LessonsList() {
  const [lessons, setLessons] = useState<(Lesson & { unit: Unit; contents_count: number })[]>([])
  const [units, setUnits] = useState<Unit[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterUnit, setFilterUnit] = useState<string>('')
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; lesson: Lesson | null }>({
    open: false,
    lesson: null
  })
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [lessonsRes, unitsRes] = await Promise.all([
        supabase
          .from('lessons')
          .select(`
            *,
            unit:units(*),
            contents:contents(count)
          `)
          .order('unit_id', { ascending: true })
          .order('order_index', { ascending: true }),
        supabase.from('units').select('*').order('unit_number', { ascending: true })
      ])

      if (lessonsRes.error) throw lessonsRes.error
      if (unitsRes.error) throw unitsRes.error

      const lessonsWithCount = (lessonsRes.data || []).map((l: any) => ({
        ...l,
        title: l.title || l.title_es || l.title_en || l.name_es || l.name_en || '',
        description: l.description || l.description_es || l.description_en || '',
        order_num: l.order_num ?? l.order_index ?? l.lesson_number ?? 0,
        contents_count: l.contents?.[0]?.count || 0
      }))

      const mappedUnits = (unitsRes.data || []).map((u: any) => ({
        ...u,
        title: u.title || u.title_es || u.title_en || u.name_es || u.name_en || '',
        order_num: u.order_num ?? u.unit_number ?? 0,
      }))

      setLessons(lessonsWithCount)
      setUnits(mappedUnits)
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteModal.lesson) return

    setIsDeleting(true)
    try {
      const { error } = await supabase
        .from('lessons')
        .delete()
        .eq('id', deleteModal.lesson.id)

      if (error) throw error

      setLessons(lessons.filter(l => l.id !== deleteModal.lesson?.id))
      setDeleteModal({ open: false, lesson: null })
    } catch (error) {
      console.error('Error deleting lesson:', error)
      alert('Error al eliminar la lección. Asegúrate de que no tenga contenidos asociados.')
    } finally {
      setIsDeleting(false)
    }
  }

  const filteredLessons = lessons.filter(lesson => {
    const matchesSearch = (lesson.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lesson.description || '').toLowerCase().includes(searchTerm.toLowerCase())
    const matchesUnit = !filterUnit || lesson.unit_id === filterUnit
    return matchesSearch && matchesUnit
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lecciones</h1>
          <p className="text-gray-600">Gestiona las lecciones del curso</p>
        </div>
        <Link
          to="/admin/lessons/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nueva Lección
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar lecciones..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <select
            value={filterUnit}
            onChange={(e) => setFilterUnit(e.target.value)}
            className="pl-10 pr-8 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] appearance-none bg-white"
          >
            <option value="">Todas las unidades</option>
            {units.map(unit => (
              <option key={unit.id} value={unit.id}>
                Unidad {unit.order_num}: {unit.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Lessons Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Orden
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lección
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unidad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contenidos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredLessons.length > 0 ? (
                filteredLessons.map((lesson) => (
                  <tr key={lesson.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#0055A4]/10 text-[#0055A4] font-semibold">
                        {lesson.order_num}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-[#0055A4]/10">
                          <BookOpen className="w-5 h-5 text-[#0055A4]" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{lesson.title}</p>
                          <p className="text-sm text-gray-500 line-clamp-1">{lesson.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2.5 py-1 bg-green-100 text-green-700 rounded-lg text-sm">
                        Unidad {lesson.unit?.order_num ?? ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{lesson.contents_count} contenidos</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        lesson.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {lesson.is_active ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/lessons/${lesson.id}`}
                          className="p-2 text-gray-500 hover:text-[#0055A4] hover:bg-[#0055A4]/5 rounded-lg transition-colors"
                          title="Visualizar"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        <Link
                          to={`/admin/lessons/${lesson.id}/edit`}
                          className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => setDeleteModal({ open: true, lesson })}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No se encontraron lecciones</p>
                    <Link
                      to="/admin/lessons/new"
                      className="text-[#0055A4] hover:underline text-sm mt-2 inline-block"
                    >
                      Crear primera lección
                    </Link>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-full bg-red-100">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Eliminar Lección</h3>
            </div>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar la lección "{deleteModal.lesson?.title}"?
              Esta acción no se puede deshacer y eliminará todos los contenidos y ejercicios asociados.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal({ open: false, lesson: null })}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
